import { useEffect, useState, type ReactNode } from 'react'
import { I18nextProvider } from 'react-i18next'
import i18n, { didTermsFailToLoad, initI18n } from './i18n'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'

/**
 * Waits for i18next (see `initI18n`), shows a loading state on a first visit,
 * and a non-blocking banner if the backend is unreachable (the bundled
 * `en-standard` still renders, so the app stays usable offline).
 *
 * `main.tsx` starts init before rendering, so on a warm load `i18n.isInitialized`
 * is already true here and the loading state never paints.
 */
export function I18nProvider({ children }: { children: ReactNode }) {
  const [ready, setReady] = useState(i18n.isInitialized)
  const [degraded, setDegraded] = useState(didTermsFailToLoad)

  useEffect(() => {
    let cancelled = false
    // `failedLoading` fires when the HTTP backend can't reach the API; the chained
    // backend still falls through to the bundled base, so we only warn.
    const onFailed = () => setDegraded(true)
    i18n.on('failedLoading', onFailed)
    // Init starts before the first render, so a failure may already be recorded.
    if (didTermsFailToLoad()) setDegraded(true)
    void initI18n().then(() => {
      if (!cancelled) setReady(true)
    })
    return () => {
      cancelled = true
      i18n.off('failedLoading', onFailed)
    }
  }, [])

  if (!ready) {
    return (
      <div className="flex h-screen items-center justify-center text-sm text-muted-foreground">
        Loading terminology…
      </div>
    )
  }

  return (
    <I18nextProvider i18n={i18n}>
      {degraded && (
        <div className="p-2">
          <Alert variant="destructive">
            <AlertTitle>Terminology service unavailable</AlertTitle>
            <AlertDescription>
              Using the bundled standard terminology. Custom overrides won&apos;t load until the
              backend is reachable.
            </AlertDescription>
          </Alert>
        </div>
      )}
      {children}
    </I18nextProvider>
  )
}
